import React from 'react';
import { useReactiveVar } from '@apollo/client';

import { LoadingOverlay } from '@occc/ui-design-system';
import config from '@occc/config';
import { useChainStatus } from '@occc-app/Chain/hooks';
import { accessTokenVar } from '@occc-app/Auth/authReactiveVariables';

import {
  AccessControlProvider,
  defaultAccessControlMatrix,
  liteVersionAccessControlMatrix,
} from '@occc/utils-access-control';
import { AppContext } from './AppContext';
import { useGetAppContextData } from './hooks/use-get-app-context-data.hook';
import { SomethingWrong } from './components/SomethingWrong';

import { AccessControlContextProvider as PolicyBasedAccessControlProvider } from '../PolicyBasedAccessControl';

type AppContextProviderProps = {
  children?: React.ReactNode;
};

const AppContextProvider = (props: AppContextProviderProps) => {
  const hasAccessToken = useReactiveVar(accessTokenVar);

  const { loading: chainLoading, error: chainError } = useChainStatus();
  const {
    appContextValue,
    error: contextDataError,
    loading: isContextDataLoading,
    currentUserAbilities,
  } = useGetAppContextData({
    skip: !hasAccessToken,
  });

  const loading = chainLoading || isContextDataLoading;
  const error = chainError || contextDataError;

  if (error) {
    return <SomethingWrong />;
  }

  if (loading) {
    return <LoadingOverlay loaderSize={120} />;
  }

  const accessControlMatrix = config.isLiteVersion
    ? liteVersionAccessControlMatrix
    : defaultAccessControlMatrix;

  return (
    <AppContext.Provider value={appContextValue}>
      <PolicyBasedAccessControlProvider value={currentUserAbilities}>
        <AccessControlProvider
          userRole={appContextValue.user?.userRole ?? null}
          userScopedRole={appContextValue?.retailerNetworkHighestRoleUser?.role ?? null}
          accessControlMatrix={accessControlMatrix}
        >
          {props.children}
        </AccessControlProvider>
      </PolicyBasedAccessControlProvider>
    </AppContext.Provider>
  );
};

export default AppContextProvider;
